import { useState, useEffect } from 'react';
import { SATQuestion } from '../algebra-practice/data/algebraQuestions';

interface SATQuestionCardProps {
  question: SATQuestion;
  questionNumber: number;
  totalQuestions: number;
  selectedAnswer: string | null;
  onAnswerSelect: (answer: string) => void;
  showSolution: boolean;
}

export default function SATQuestionCard({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswer,
  onAnswerSelect,
  showSolution,
}: SATQuestionCardProps) {
  const [revealSolution, setRevealSolution] = useState(false);

  useEffect(() => {
    if (selectedAnswer !== null) setRevealSolution(true);
    else setRevealSolution(false);
  }, [selectedAnswer, question.id]);

  const isAnswered = selectedAnswer !== null;
  const isCorrect = selectedAnswer === question.correctAnswer;

  const getOptionClass = (label: string) => {
    if (!isAnswered) {
      return 'bg-white hover:bg-emerald-50 border-2 border-gray-200 hover:border-emerald-400 cursor-pointer';
    }
    if (label === question.correctAnswer) {
      return 'bg-emerald-100 border-2 border-emerald-500';
    }
    if (label === selectedAnswer && label !== question.correctAnswer) {
      return 'bg-red-100 border-2 border-red-500';
    }
    return 'bg-gray-50 border-2 border-gray-200 opacity-60';
  };

  const getOptionIcon = (label: string) => {
    if (!isAnswered) return null;
    if (label === question.correctAnswer) {
      return <i className="ri-check-line text-emerald-600 text-xl"></i>;
    }
    if (label === selectedAnswer && label !== question.correctAnswer) {
      return <i className="ri-close-line text-red-600 text-xl"></i>;
    }
    return null;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Question Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-emerald-600">
            Question {questionNumber} of {totalQuestions}
          </span>
          <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
            {question.subtopic}
          </span>
          {isAnswered && (
            <span className={`text-sm font-semibold ${isCorrect ? 'text-emerald-600' : 'text-red-600'}`}>
              {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
            </span>
          )}
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <p className="text-lg text-gray-800 leading-relaxed">{question.question}</p>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-6">
        {question.options.map((opt) => (
          <button
            key={opt.label}
            onClick={() => !isAnswered && onAnswerSelect(opt.label)}
            disabled={isAnswered}
            className={`w-full text-left p-4 rounded-lg transition-all duration-200 flex items-center justify-between ${getOptionClass(opt.label)}`}
          >
            <div className="flex items-start gap-4 flex-1">
              <span className="font-semibold text-gray-700 min-w-[24px]">{opt.label}.</span>
              <span className="text-gray-800 flex-1">{opt.value}</span>
            </div>
            {getOptionIcon(opt.label)}
          </button>
        ))}
      </div>

      {/* Solution Panel */}
      {(revealSolution || showSolution) && isAnswered && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6 border-2 border-emerald-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <i className="ri-lightbulb-line text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Step-by-Step Solution</h3>
          </div>
          <ol className="space-y-2 mb-4">
            {question.solution.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700">
                <span className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <div className="bg-white/70 rounded-lg p-4 border border-emerald-200">
            <p className="text-sm text-gray-700 leading-relaxed">
              <span className="font-semibold text-emerald-700">Key Concept: </span>
              {question.solution.explanation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
